/**
 * Departments (`/departments`): organisational units, each activated by a license key of its own.
 * The control center's own section for them is `controlCenter.departments`.
 *
 * `errors` is keyed by the server's error codes and looked up dynamically
 * (`utils/department/errors.ts`), as is `license.failures`, so those key names are a contract with
 * the API rather than free-form.
 */

export default {
	departments: {
		title: "Departments",
		default: "Default",
		list: {
			create: "Create department",
			empty: {
				title: "You are not in a department yet",
				hint: "A department is an organisational unit with a license key of its own. Ask one of its owners to add you.",
				createHint:
					"A department is an organisational unit with a license key of its own. Create one with the key LiveReader issued for it, or ask an owner to add you to theirs.",
			},
			column: {
				department: "Department",
				role: "Your role",
				license: "License",
			},
			everyone: "Everyone is a member",
		},
		create: {
			title: "Create a department",
			note: "The license key is checked with the license server first. Only its hash is sent, and the key is stored encrypted.",
			name: "Name",
			description: "Description",
			licenseKey: "License key",
			licenseKeyHelp:
				"The key LiveReader issued for this department. Nothing is created if the license server refuses it.",
			submit: "Check key and create",
		},
		page: {
			sections: {
				overview: "Overview",
				spaces: "Spaces",
				members: "Members",
				seats: "Seats",
				roles: "Roles",
			},
			gone: {
				deleted: "This department was deleted",
				deletedNote: "Its members and roles went with it.",
				unavailable: "This department is not available to you",
				unavailableNote: "It was deleted, or you are no longer a member of it.",
				back: "Back to departments",
			},
		},
		general: {
			title: "Details",
			name: "Name",
			description: "Description",
			members: "Members",
			projects: "Projects",
			dataspaces: "Dataspaces",
			created: "Created",
			yourRole: "Your role",
			saved: "Saved the department.",
			changedElsewhere:
				"Someone else changed this department while you were editing. Your edit is kept; discard it to see theirs.",
		},
		spaces: {
			title: "Spaces",
			empty: "No project or dataspace is in this department yet.",
			noAccess: "No access",
			column: {
				name: "Name",
				kind: "Kind",
			},
			kind: {
				project: "Project",
				dataspace: "Dataspace",
			},
			makePersonal: "Make {name} personal",
			personalConfirm: "Make {name} personal?",
			madePersonal: "{name} is personal now.",
			moved: "Moved {n} space into this department. | Moved {n} spaces into this department.",
			move: {
				action: "Move spaces here",
				title: "Move spaces into {department}",
				note: "A space that moves in has its department access reset: this department's owners become its admins, and nobody else reaches it through the department until you map more roles. People added to it by hand keep their access.",
				none: "You have no project or dataspace you may move here. Moving takes the right to delete the space and, for one that is in another department, the right to manage that department's spaces.",
				confirm: "Move {n} space | Move {n} spaces",
			},
			choose: {
				search: "Search spaces…",
				label: "Spaces",
			},
		},
		license: {
			title: "License",
			note: "Checked with the license server every hour",
			unnamed: "Valid license",
			valid: "Licensed to {label}.",
			validNote: "Last confirmed {date}.",
			overdue:
				"The license server has not confirmed this license lately. It lapses in {n} day unless a check succeeds. | The license server has not confirmed this license lately. It lapses in {n} days unless a check succeeds.",
			overdueNote: "It lapses on {date}.",
			expired: "No confirmed license since {date}.",
			expiredNote:
				"The license server has not confirmed it for 30 days. Replace the key, or contact LiveReader.",
			missing: "No license is confirmed for this department.",
			missingNote: "Replace the key with one LiveReader issued for this department.",
			facts: {
				label: "Licensed to",
				status: "Status",
				answer: "License server's answer",
				floatingSeats: "Floating seats",
				namedSeats: "Named seats",
				designer: "Designer",
				keyHint: "Key",
				lastCheck: "Last confirmed",
				graceEnds: "Grace period ends",
				graceLeft: "{n} day left | {n} days left",
			},
			status: {
				valid: "Valid",
				overdue: "Overdue",
				expired: "Expired",
				missing: "Missing",
			},
			answers: {
				granted: "Granted",
				disabled: "Disabled",
				"bound-elsewhere": "Bound to another server",
				unknown: "Key not known",
				invalid: "Not a valid key",
				unconfirmed: "No answer",
			},
			on: "Members may use it",
			off: "Not included",
			lastError: "The last check failed ({date})",
			failures: {
				department_license_disabled:
					"The license server says this license is disabled. The grace period keeps running from the last confirmation.",
				department_license_bound_elsewhere:
					"The license server says this key is bound to another server.",
				department_license_unknown: "The license server does not know this key.",
				department_license_invalid: "The license server says this is not a valid key.",
				department_license_unconfirmed:
					"The license server did not answer for this key. That is not a revocation: the license keeps working while the grace period runs.",
			},
			rekey: {
				action: "Replace key",
				label: "New license key",
				help: "Checked with the license server before it is saved; if it is refused, the key in use stays. The department keeps its members, roles and spaces.",
				submit: "Check and save",
				saved: "The new license key is confirmed and saved.",
			},
			reveal: "Show the key",
			hide: "Hide the key",
		},
		remove: {
			title: "Delete department",
			note: "Deleting a department removes its members and roles. A department with a project or a dataspace in it cannot be deleted, and nor can the default one.",
			action: "Delete department",
			confirm: "Delete {name}?",
			confirmNote: "Its members and roles go with it. This cannot be undone.",
			deleted: "Deleted {name}.",
		},
		members: {
			title: "Members",
			invite: "Invite by email",
			role: "Role",
			person: "Person",
			you: "You",
			empty: "This department has no members of its own yet.",
			roleChanged: "Changed the role of {name}.",
			remove: "Remove {name}",
			removeConfirm: "Remove {name}?",
			removeNote: "They lose what the department's roles gave them at once.",
			removeNoteDefault:
				"Everyone is a member of the default department, so they stay one, with its default role instead of this one.",
			removed: "Removed {name}.",
			leave: "Leave {department}",
			leaveConfirm: "Leave {name}?",
			leaveAction: "Leave",
			leaveNote: "You lose what your role gave you here. An owner can invite you again.",
			left: "You left {name}.",
			implicit:
				"You are a member because everyone is a member of the default department. There is no membership of your own to leave or change.",
		},
		invitations: {
			dialogTitle: "Invite to this department",
			note: "The invited person gets a link by email, and once their account provider has verified the address, can also answer from the Invitations section of their account. They join with the role you pick, and only when they accept: nobody is added to a department without being asked.",
		},
		seats: {
			title: "Seats",
			note: "Opening a process takes a seat, whether you then work in it or only watch. The process list and the settings take none.",
			refresh: "Refresh",
			kind: {
				named: "Named seat",
				floating: "Floating seat",
			},
			noneInLicense: "None in the license",
			floating: {
				label: "Floating seats",
				reading: "{used} of {total} in use",
			},
			named: {
				label: "Named seats",
				reading: "{used} of {total} held",
				title: "Named seats",
				note: "A named seat is reserved for one member, who gets in even when every floating seat is taken. The license's named seats go to the first members given one, in the order they were given it. Giving or taking one never removes anybody.",
				column: "Named seat",
				switch: "Named seat for {name}",
				holds: "Holds a seat",
				waiting:
					"Waiting: the license has no named seats | Waiting: the license has one named seat | Waiting: the license has {n} named seats",
				waitingBanner:
					"One member is given a named seat the license has no room for | {n} members are given a named seat the license has no room for",
				waitingNote:
					"They use a floating seat like everybody else until a named seat is free. Nothing is lost: the license's named seats go to the members given one first.",
				given: "{name} has a named seat now.",
				taken: "{name} has no named seat now.",
				empty: "This department has no members of its own to give a named seat.",
			},
			peak: "Most floating seats in use at once",
			refused: "Requests for a floating seat that were refused",
			since: "Counted since {date}.",
			nothingCounted: "Nothing has been counted yet.",
			over: {
				title: "More floating seats are in use than the license now has ({used} of {total})",
				note: "Nobody is removed: whoever holds a seat keeps it. Nobody else gets one until fewer than {total} are in use.",
			},
			active: {
				title: "In use now",
				note: "Who holds a seat right now. It changes as people open and leave processes; refresh to read it again.",
				column: "Seat",
				empty: "Nobody holds a seat right now.",
			},
		},
		roles: {
			title: "Department roles",
			locked: "Locked",
			memberCount: "No members | {n} member | {n} members",
			sections: {
				department: "Department",
				people: "People",
				spaces: "Spaces",
				license: "License",
			},
			ops: {
				read: "See the department",
				update: "Edit name and description",
				delete: "Delete the department",
				members: "Manage members",
				invitations: "Manage invitations",
				roles: "Manage roles",
				seats: "Manage seats",
				spaces: "Manage spaces",
				moveIn: "Move spaces in",
				projects: "Create projects",
				dataspaces: "Create dataspaces",
				license: "Replace the license key",
			},
		},
		errors: {
			invalid_name: "A name is 1 to 100 characters.",
			invalid_description: "A description is up to 2000 characters.",
			department_name_taken: "A department with this name exists already.",
			department_key_unknown:
				"The license server does not know this key. Check it and try again.",
			department_key_invalid: "The license server says this is not a valid department key.",
			invalid_license_key: "A license key is a single word, without spaces.",
			department_key_disabled:
				"The license server says this department's license is disabled. Contact LiveReader.",
			department_key_bound_elsewhere:
				"This key is bound to another server. Contact LiveReader to move it.",
			department_key_in_use: "Another department on this server already uses this key.",
			department_key_unconfirmed:
				"The license server gave no answer for this key, so nothing was saved. Try again later.",
			license_required:
				"This server has no valid license, so a department cannot be activated or have its key replaced. A server administrator can enter one in the control center.",
			license_disabled:
				"The license server says this server's license is disabled. Contact LiveReader.",
			license_server_unreachable: "The license server could not be reached. Try again later.",
			license_response_invalid: "The license server's answer could not be read.",
			department_not_empty:
				"A project or a dataspace is still in this department. Move or delete it first.",
			department_is_default: "The default department cannot be deleted.",
			last_owner: "A department keeps at least one owner. Make someone else an owner first.",
			default_department:
				"Everyone is a member of the default department, so it cannot be left.",
			already_member: "This user is a member of the department already.",
			already_invited:
				"An invitation is already pending for this email. Cancel or resend it instead.",
			self_invite: "You cannot invite yourself.",
			invitation_mail_disabled:
				"Invitation mail is switched off, or this server cannot send mail, so an invitation cannot be sent again. Cancel it and invite again to get a link to pass on yourself.",
			invalid_role: "That is not a role of this department.",
			invalid_entitlement: "A department role holds department permissions only.",
			role_exists: "A role with this name exists in the department already.",
			role_in_use: "Members still hold this role. Give them another one first.",
			role_locked:
				"What the owner role grants is fixed. Duplicate it to make a role you can edit.",
			role_built_in: "A built-in role can be neither renamed nor deleted.",
			not_a_member:
				"Only a member of their own can be given a named seat. This person is a member because everyone is.",
			missing_entitlement: "Your system role does not allow this.",
		},
	},
};
